/**
 * Tracking Prevention Initialization
 *
 * This file sets up comprehensive tracking prevention handling at application startup.
 * It should be imported early in the application lifecycle to ensure all storage
 * access is properly handled.
 */
import { isTrackingBlocked, safeStorage, suppressTrackingPreventionMessages } from './trackingUtils';
import { logger } from "./logger";
// import { logger } from "./logger";
/**
 * Initialize tracking prevention handling
 */
export const initializeTrackingPrevention = (): void => {
    try {
        logger.debug('[Tracking Prevention] Initializing...');
        // Suppress tracking prevention console messages
        suppressTrackingPreventionMessages();
        // Use the centralized error suppression instead of duplicating logic
        // The errorSuppressor is already applied in main.tsx
        // Check if tracking is blocked
        const blocked = isTrackingBlocked();
        if (blocked) {
            logger.info('[Tracking Prevention] Browser storage access is blocked. Using in-memory fallback.');
            // Set a flag in safe storage to indicate tracking prevention is active
            safeStorage.setItem('tracking_prevention_active', 'true');
            safeStorage.setItem('tracking_prevention_timestamp', new Date().toISOString());
            // DON'T clear existing auth data - let the persistent memory storage handle it
            // The persistent memory storage will preserve auth data across page refreshes
            // Only clear if we're switching from non-blocked to blocked state
            const wasPreviouslyBlocked = safeStorage.getItem('tracking_prevention_active') === 'true';
            if (!wasPreviouslyBlocked) {
                logger.auth('[Tracking Prevention] First time detecting tracking prevention - preserving existing auth data in memory.');
            }
            // Log helpful information for users
            logger.info('[Tracking Prevention] Some features may not persist between sessions.');
            logger.info('[Tracking Prevention] Consider enabling cookies and site data for this site.');
            // Set a flag to reduce API calls that might fail
            safeStorage.setItem('reduce_api_calls', 'true');
        }
        else {
            logger.debug('[Tracking Prevention] Browser storage access is available.');
            safeStorage.removeItem('tracking_prevention_active');
            safeStorage.removeItem('reduce_api_calls');
        }
        logger.debug('[Tracking Prevention] Initialization complete');
    }
    catch (error) {
        logger.error('[Tracking Prevention] Initialization failed:', error, {});
        // Set fallback flags even if initialization fails
        try {
            safeStorage.setItem('tracking_prevention_active', 'true');
            safeStorage.setItem('reduce_api_calls', 'true');
        }
        catch (fallbackError) {
            // If even fallback fails, just continue
            logger.warn('[Tracking Prevention] Fallback setup also failed:', { "arg0": fallbackError });
        }
    }
};
/**
 * Check if tracking prevention is currently active
 */
export const isTrackingPreventionActive = (): boolean => {
    return safeStorage.getItem('tracking_prevention_active') === 'true';
};
/**
 * Get tracking prevention status information
 */
export const getTrackingPreventionStatus = () => {
    const active = isTrackingPreventionActive();
    const timestamp = safeStorage.getItem('tracking_prevention_timestamp');
    return {
        active,
        timestamp: timestamp ? new Date(timestamp) : null,
        browserBlocked: isTrackingBlocked(),
        recommendations: active ? [
            'Enable cookies and site data for this site',
            'Check browser privacy settings',
            'Disable tracking prevention for this domain'
        ] : []
    };
};
